import dynamic from 'next/dynamic'
import { useState } from 'react'
import Script from 'next/script'
import { GetServerSideProps } from 'next'
import { useRouter } from 'next/router'
import { QueryClient, useQuery } from 'react-query'
import isEmpty from 'lodash/isEmpty'
import { getCustomDeviceDetails } from '@/root/utils/common/web/get-mobile-os'
import { IND_STOCKS_BASEPATH } from '@/root/api/stocks/apiConstants'
import { fetchRequest } from '@/root/utils/network-adapter/fetch-request'
import { TAssetDetailsData } from '@/components/Stocks/Widgets/types'
import SEOHeader from '@/components/Common/SEOHeader'
import styles from '@/components/TVChartContainer/TVChartContainerDweb/index.module.css'
import { IUserStateHash } from '@/components/Common/user-provider/types/user-state-hash'
import { DOMAIN } from '@/root/constants'
import { INDSTOCKS_HOST } from '@/root/constants/common'

const TVChartContainer = dynamic(
  () => import('@/components/TVChartContainer/TVChartContainerDweb'),
  { ssr: false },
)

const DwebTrading = ({
  headers,
  memberid,
  user_id,
  apiResponseData,
}: {
  headers: {
    platform?: string
    Authorization?: string
  }
  memberid?: string
  user_id?: string
  apiResponseData?: TAssetDetailsData
}) => {
  const [isScriptReady, setIsScriptReady] = useState(false)

  /* ROUTER */
  const router = useRouter()
  const { query = {} } = router
  const { stockSlug: slug = '' } = query

  const { data } = useQuery<TAssetDetailsData>(
    `tradingviewDwebDetails${slug}`,
    () =>
      fetchRequest({
        config: {
          headers,
        },
        timeout: 0,
        url: `${IND_STOCKS_BASEPATH}/catalog/v2/get-entity-details/${slug}?response_format=json&catalog-required=false&graph-optional=true&investment-optional=true${
          memberid ? `&member_id=${memberid}` : ''
        }`,
      }),
    {
      retry: 0,
      staleTime: 300,
      ...(!isEmpty(apiResponseData) && { initialData: apiResponseData }),
      enabled: isEmpty(apiResponseData),
    },
  )

  const { data: userStateData } = useQuery<IUserStateHash>(
    [`user-state-api`],
    () =>
      fetchRequest({
        config: {
          headers,
        },
        timeout: 0,
        url: `${IND_STOCKS_BASEPATH}/portfolio/get-user-state?meta-data=hash${
          memberid ? `&member_id=${memberid}` : ''
        }`,
      }),
    {
      retry: 0,
      staleTime: 300,
      enabled: !!headers.Authorization,
    },
  )

  const shouldIndex =
    apiResponseData?.catalog?.entity_basic?.segment === 'INDEX' ||
    (apiResponseData?.catalog?.entity_basic?.market_cap_in_currency ?? 0) >
      10000

  const name = apiResponseData?.catalog?.entity_basic?.name
  const canonicalUrl = `${DOMAIN}${router.asPath.split('?')[0]}`

  return (
    <div className="h-screen">
      <SEOHeader
        noIndexNoFollow={!shouldIndex}
        data={{
          title: `${name} Share Price Live Chart Today - ${name} Stock Price Charts`,
          keywords: `${name} Charts, ${name} Share Price Chart`,
          description: `Discover ${name} charts today: View Live ${name} chart share price for NSE and BSE.`,
          canonicalUrl,
        }}
      />
      <Script
        src="/static/datafeeds/udf/dist/bundle.js"
        strategy="lazyOnload"
        onReady={() => {
          setIsScriptReady(true)
        }}
      />
      <div className={styles.TVChartContainer}>
        {isScriptReady && (
          // @ts-ignore
          <TVChartContainer
            headers={headers}
            slug={slug}
            apiResponseData={data}
            user_id={user_id}
            hash_id={userStateData?.data?.user_hash_meta_data?.hash_id}
          />
        )}
      </div>
    </div>
  )
}

export const getServerSideProps: GetServerSideProps = async context => {
  const queryClient = new QueryClient({})
  const token = context.query.token ?? context.req.cookies.token ?? null
  const headers = {
    ...(token && { Authorization: `Bearer ${token}` }),
    Platform: 'web',
  }
  const member_id =
    (context.query.memberId ? Number(context.query.memberId) : null) ??
    (context.req.cookies.memberId
      ? Number(context.req.cookies.memberId)
      : null) ??
    null

  const deviceDetails = getCustomDeviceDetails(
    context.req.headers['user-agent'],
  )

  const isMobile =
    deviceDetails.platform === 'unknown' ? null : deviceDetails.isMobile

  const apiResponseData = await queryClient.fetchQuery<TAssetDetailsData>(
    `tradingviewDwebDetails${context.params?.stockSlug}`,
    async () => {
      let data

      try {
        data = await fetchRequest({
          config: {
            headers,
          },
          timeout: 1000,
          url: `${IND_STOCKS_BASEPATH}/catalog/v2/get-entity-details/${context
            .params
            ?.stockSlug}?response_format=json&catalog-required=false&graph-optional=true&investment-optional=true${
            member_id ? `&member_id=${member_id}` : ''
          }`,
        })
      } catch (e: unknown) {
        console.info(e)
      }
      return data ?? {}
    },
    {
      retry: 0,
      staleTime: 300,
    },
  )

  if (context.req.headers.host === INDSTOCKS_HOST) {
    context.res.setHeader('X-Middleware-Caching', 'false')
  }

  return {
    props: {
      isMobile,
      headers,
      memberid: member_id,
      apiResponseData,
    },
  }
}

export default DwebTrading
